import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./itineraries.css";
import {
  Plus,
  Search,
  MapPin,
  Users,
  Calendar,
  FileText,
  Plane,
  Hotel,
  Phone,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Edit,
  Trash2
} from "lucide-react";

import CreateItineraryDialog from "../components/itineraries/CreateItineraryDialog";

// API calls - Based on your backend controllers
const API_BASE = "https://crmapi.zauntrip.com/api"; // Changed from 4001 to 4000

const fetchItineraries = async () => {
  try {
    const response = await fetch(`${API_BASE}/itineraries`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    const data = await response.json();
    console.log("Fetched itineraries:", data); // Debug log
    return data.success ? data.data : [];
  } catch (error) {
    console.error("Error fetching itineraries:", error);
    return [];
  }
};

const deleteItinerary = async (id) => {
  try {
    const response = await fetch(`${API_BASE}/itineraries/${id}`, {
      method: "DELETE",
    });
    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error("Error deleting itinerary:", error);
    return false;
  }
};

export default function Itineraries() {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadItineraries();
  }, []);

  const loadItineraries = async () => {
    setLoading(true);
    const data = await fetchItineraries();
    setItineraries(data);
    setLoading(false);
  };

  const handleCreateItinerary = async (formData) => {
    setIsCreating(true);
    
    try {
      console.log("Creating itinerary with data:", formData);
      
      // Step 1: Create the main itinerary
      const itineraryData = {
        title: formData.title,
        preparedFor: formData.preparedFor.filter(t => t.trim() !== ""),
        startDate: formData.startDate,
        endDate: formData.endDate,
        totalDays: formData.totalDays,
        destinations: formData.destinations.filter(d => d.city.trim() !== "" && d.country.trim() !== ""),
        notes: formData.notes,
        status: formData.status,
        // Initialize empty arrays - will be populated by backend
        days: [],
        flights: [],
        hotels: [],
        localContacts: []
      };

      console.log("Creating main itinerary:", itineraryData);
      
      // Create the itinerary - Based on itineraryController.js
      const response = await fetch(`${API_BASE}/itineraries/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(itineraryData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response error:", errorText);
        throw new Error(`Failed to create itinerary: ${response.status}`);
      }

      const result = await response.json();
      console.log("Itinerary creation response:", result);
      
      if (!result.success) {
        throw new Error(result.message || "Failed to create itinerary");
      }

      const itineraryId = result.data._id;
      console.log("Itinerary created with ID:", itineraryId);

      // Step 2: Create days - Based on itineraryController.js addDay method
      const daysPromises = [];
      if (formData.days && formData.days.length > 0) {
        console.log("Creating days:", formData.days);
        for (const day of formData.days) {
          if (day.title.trim() || day.city.trim() || day.description.trim()) {
            const dayData = {
              ...day,
              itineraryId: itineraryId
            };
            
            console.log("Sending day data:", dayData);
            
            daysPromises.push(
              fetch(`${API_BASE}/itineraries/day/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dayData),
              }).then(async res => {
                if (!res.ok) {
                  const errorText = await res.text();
                  console.error("Failed to create day:", errorText);
                  throw new Error(`Failed to create day: ${res.status}`);
                }
                return res.json();
              })
            );
          }
        }
      }

      // Step 3: Create flights - Based on flightController.js
    const flightsPromises = [];
if (formData.flights && formData.flights.length > 0) {
  console.log("Creating flights:", formData.flights);
  for (const flight of formData.flights) {
    if (flight.flightNumber.trim() || flight.airline.trim()) {
      const flightData = {
        ...flight,
        itineraryId: itineraryId
      };
      
      console.log("Sending flight data:", flightData);
      
      flightsPromises.push(
        fetch(`${API_BASE}/itineraries/flights/create`, {  // CHANGED THIS LINE
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(flightData),
        }).then(async res => {
          if (!res.ok) {
            const errorText = await res.text();
            console.error("Failed to create flight:", errorText);
            throw new Error(`Failed to create flight: ${res.status}`);
          }
          return res.json();
        })
      );
    }
  }
}

      // Step 4: Create hotels - Based on hotelController.js
      const hotelsPromises = [];
if (formData.hotels && formData.hotels.length > 0) {
  console.log("Creating hotels:", formData.hotels);
  for (const hotel of formData.hotels) {
    if (hotel.name.trim() || hotel.city.trim()) {
      const hotelData = {
        ...hotel,
        itineraryId: itineraryId,
      };
      
      console.log("Sending hotel data:", hotelData);
      
      hotelsPromises.push(
        fetch(`${API_BASE}/itineraries/hotels/create`, {  // CHANGED THIS LINE
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(hotelData),
        }).then(async res => {
          if (!res.ok) {
            const errorText = await res.text();
            console.error("Failed to create hotel:", errorText);
            throw new Error(`Failed to create hotel: ${res.status}`);
          }
          return res.json();
        })
      );
    }
  }
}

      // Step 5: Create local contacts - Based on contactController.js
      const contactsPromises = [];
if (formData.localContacts && formData.localContacts.length > 0) {
  console.log("Creating local contacts:", formData.localContacts);
  for (const contact of formData.localContacts) {
    if (contact.name.trim() || contact.company.trim()) {
      const contactData = {
        ...contact,
        itineraryId: itineraryId
      };
      
      console.log("Sending contact data:", contactData);
      
      contactsPromises.push(
        fetch(`${API_BASE}/itineraries/contacts/create`, {  // CHANGED THIS LINE
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(contactData),
        }).then(async res => {
          if (!res.ok) {
            const errorText = await res.text();
            console.error("Failed to create contact:", errorText);
            throw new Error(`Failed to create contact: ${res.status}`);
          }
          return res.json();
        })
      );
    }
  }
}

      // Wait for all promises to complete
      console.log("Waiting for all API calls to complete...");
      
      // Execute all promises and collect results
      const allPromises = [
        ...daysPromises,
        ...flightsPromises,
        ...hotelsPromises,
        ...contactsPromises
      ];
      
      if (allPromises.length > 0) {
        await Promise.all(allPromises);
      }
      
      console.log("All data created successfully!");
      
      // Reload itineraries and navigate
      await loadItineraries();
      setShowCreateDialog(false);
      setIsCreating(false);
      navigate(`/itineraries/${itineraryId}`);
      
    } catch (error) {
      console.error("Error creating itinerary:", error);
      alert(`Failed to create itinerary: ${error.message}. Please check the console for details.`);
      setIsCreating(false);
    }
  };

  const handleViewDetails = (id) => {
    navigate(`/itineraries/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/itineraries/${id}/edit`);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    const success = await deleteItinerary(id);
    if (success) {
      await loadItineraries();
    } else {
      alert("Failed to delete itinerary. Please try again.");
    }
    setDeletingId(null);
    setShowDeleteConfirm(null);
  };

  const confirmDelete = (id, title) => {
    setShowDeleteConfirm({ id, title });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Confirmed":
        return <CheckCircle size={14} />;
      case "Draft":
        return <Clock size={14} />;
      case "Cancelled":
        return <XCircle size={14} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Confirmed":
        return "status-confirmed";
      case "Draft":
        return "status-draft";
      case "Cancelled":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const filteredItineraries = itineraries.filter((itinerary) => {
    const matchesSearch =
      itinerary.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      itinerary.preparedFor?.some(name => 
        name.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      itinerary.destinations?.some(dest => 
        dest.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dest.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      filterStatus === "all" || itinerary.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getDestinationsString = (destinations) => {
    if (!destinations || destinations.length === 0) return "No destinations";
    return destinations.map(d => d.city).join(", ");
  };

  return (
    <div className="itineraries-page">
      <div className="page-header">
        <div>
          <h1>Itineraries</h1>
          <p>Create and manage travel itineraries</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowCreateDialog(true)}
          disabled={isCreating}
        >
          <Plus size={16} /> 
          {isCreating ? "Creating..." : "Create Itinerary"}
        </button>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="search-box">
          <Search size={16} />
          <input
            placeholder="Search itineraries by title, traveler, or destination..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Confirmed">Confirmed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading itineraries...</p>
        </div>
      ) : (
        <>
          {/* Cards */}
          <div className="itinerary-grid">
            {filteredItineraries.map((itinerary) => (
              <div className="itinerary-card" key={itinerary._id}>
                <div className="card-header">
                  <div>
                    <h3>{itinerary.title}</h3>
                    <p className="travelers">
                      For: {itinerary.preparedFor?.join(", ") || "No travelers specified"}
                    </p>
                  </div>
                  <span className={`status-badge ${getStatusColor(itinerary.status)}`}>
                    {getStatusIcon(itinerary.status)}
                    {itinerary.status}
                  </span>
                </div>

                <div className="card-details">
                  <div>
                    <MapPin size={16} />
                    {getDestinationsString(itinerary.destinations)}
                  </div>

                  <div>
                    <Calendar size={16} />
                    {formatDate(itinerary.startDate)} - {formatDate(itinerary.endDate)}
                    {itinerary.totalDays && ` (${itinerary.totalDays} days)`}
                  </div>

                  <div className="stats-row">
                    <div className="stat-item">
                      <FileText size={16} />
                      <span>{itinerary.days?.length || 0} days</span>
                    </div>
                    
                    <div className="stat-item">
                      <Plane size={16} />
                      <span>{itinerary.flights?.length || 0} flights</span>
                    </div>
                    
                    <div className="stat-item">
                      <Hotel size={16} />
                      <span>{itinerary.hotels?.length || 0} hotels</span>
                    </div>
                    
                    <div className="stat-item">
                      <Phone size={16} />
                      <span>{itinerary.localContacts?.length || 0} contacts</span>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <div className="action-buttons">
                    <button 
                      className="btn-icon" 
                      onClick={() => handleViewDetails(itinerary._id)}
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className="btn-icon" 
                      onClick={() => handleEdit(itinerary._id)}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-icon danger" 
                      onClick={() => confirmDelete(itinerary._id, itinerary.title)}
                      disabled={deletingId === itinerary._id}
                      title="Delete"
                    >
                      {deletingId === itinerary._id ? (
                        <div className="mini-spinner"></div>
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredItineraries.length === 0 && !loading && (
            <div className="empty-state">
              <FileText size={40} />
              <h3>No itineraries found</h3>
              <p>
                {searchTerm || filterStatus !== "all"
                  ? "Try adjusting your filters"
                  : "Start by creating your first itinerary"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete itinerary "{showDeleteConfirm.title}"?
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-outline" 
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deletingId === showDeleteConfirm.id}
              >
                Cancel
              </button>
              <button 
                className="btn-danger" 
                onClick={() => handleDelete(showDeleteConfirm.id)}
                disabled={deletingId === showDeleteConfirm.id}
              >
                {deletingId === showDeleteConfirm.id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Itinerary Dialog */}
      <CreateItineraryDialog
        open={showCreateDialog}
        onClose={() => {
          if (!isCreating) {
            setShowCreateDialog(false);
          }
        }}
        onSubmit={handleCreateItinerary}
        isLoading={isCreating}
      />
    </div>
  );
}